import { TransactionDocumentEmissionEstimate } from '@lune-climate/lune'
import { Snippet, SnippetItem } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { useMemo, useState } from 'react'

import Divider from '../../components/Divider'
import { getFormattedAmount } from '../../utils/utils'

import {
    asActivityTransactionLineItemForExplanation,
    asPassengerTransportationLineItemForExplanation,
    asTransactionLineItemForExplanation,
} from './utils/transactionDocumentExplanationUtils'
import { InputWrapper } from './InputsWrapper'
import { LineItems } from './LineItems'
import { TransactionDocumentEmissionsExplanationActivityLineItemSection } from './TransactionDocumentEmissionsExplanationActivityLineItemSection'
import { TransactionDocumentEmissionsExplanationTransactionLineItemSection } from './TransactionDocumentEmissionsExplanationTransactionLineItemSection'
import { TransactionDocumentEmissionsExplanationTransportationLineItemSection } from './TransactionDocumentEmissionsExplanationTransportationLineItemSection'

export const TransactionDocumentLineItems = ({
    lineItems,
    input,
    enableEmissionFactorLink,
}: {
    lineItems: NonNullable<TransactionDocumentEmissionEstimate['lineItems']>
    input?: any
    enableEmissionFactorLink: boolean
}) => {
    const [activeTabIndex, setActiveTabIndex] = useState<string>('1')

    const activeLineItem = useMemo(() => {
        if (activeTabIndex && lineItems.length) {
            return lineItems[Number(activeTabIndex) - 1]
        }
        return null
    }, [activeTabIndex, lineItems])

    const activeLineItemFormattedAmount = useMemo(() => {
        if (activeTabIndex && lineItems.length) {
            const lineItem = lineItems[Number(activeTabIndex) - 1]
            return {
                amountAsNumber: getFormattedAmount(lineItem.mass.amount).amountAsNumber,
                amountUnit: getFormattedAmount(lineItem.mass.amount).amountUnit,
            }
        }
        return null
    }, [activeTabIndex, lineItems])

    const formattedInput = useMemo((): string | undefined => {
        if (!input) {
            return undefined
        }
        return JSON.stringify(input, null, 2)
    }, [input])

    return (
        <>
            <LineItems
                lineItems={lineItems}
                setActiveTab={(activeIndex) => setActiveTabIndex(activeIndex.toString())}
            />
            {activeLineItem && activeLineItemFormattedAmount && lineItems.length && (
                <>
                    <Divider />
                    {activeLineItem.type === 'transaction' && (
                        <TransactionDocumentEmissionsExplanationTransactionLineItemSection
                            lineItem={asTransactionLineItemForExplanation(activeLineItem).expect(
                                'Programming error: Unexpected transaction line item type',
                            )}
                            enableEmissionFactorLink={enableEmissionFactorLink}
                        />
                    )}
                    {activeLineItem.type === 'activity_transaction' && (
                        <TransactionDocumentEmissionsExplanationActivityLineItemSection
                            lineItem={asActivityTransactionLineItemForExplanation(
                                activeLineItem,
                            ).expect('Programming error: Unexpected transaction line item type')}
                            enableEmissionFactorLink={enableEmissionFactorLink}
                        />
                    )}
                    {activeLineItem.type === 'passenger_transportation' && (
                        <TransactionDocumentEmissionsExplanationTransportationLineItemSection
                            lineItem={asPassengerTransportationLineItemForExplanation(
                                activeLineItem,
                            ).expect('Programming error: Unexpected transaction line item type')}
                            enableEmissionFactorLink={enableEmissionFactorLink}
                            mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
                        />
                    )}
                    {formattedInput && (
                        <>
                            <Divider />
                            <Box>
                                <InputWrapper>
                                    <Snippet header="JSON" sx={{ maxHeight: '480px' }}>
                                        <SnippetItem language={'plain'} toCopy={formattedInput}>
                                            {formattedInput}
                                        </SnippetItem>
                                    </Snippet>
                                </InputWrapper>
                            </Box>
                        </>
                    )}
                </>
            )}
        </>
    )
}

export default TransactionDocumentLineItems
